import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  Observable,
  switchMap,
  map,
  catchError,
  of,
  from,
  take,
  forkJoin
} from 'rxjs';
import * as fromReviewsActions from '../actions/reviews.actions';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import {
  AngularFirestore,
  DocumentReference,
  Query,
  QueryFn
} from '@angular/fire/compat/firestore';
import {
  Review,
  ReviewFirebaseResponse,
  ReviewForm,
  ReviewFormFirebaseResponse,
  ReviewsFilter
} from 'src/app/models/reviews.model';
import { User } from 'src/app/models/user.model';
import { Router } from '@angular/router';
import { States } from 'src/app/enums/state.enum';
import firebase from 'firebase/compat/app';
import { CompleteReviewSuccessAction } from '../actions/reviews.actions';
import { FrequencyReviewsTypes } from 'src/app/enums/frequencyReviewsTypes';

@Injectable()
export class ReviewsEffects {
  private readonly collectionName = 'reviews';
  constructor(
    private readonly action$: Actions,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore,
    private readonly router: Router
  ) {}

  // Load Reviews Form
  loadReviewsForm$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.LoadReviewsFormsAction),
      switchMap(({ trainer }: { trainer: User }) => {
        const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;

        return this.firebaseService
          .getDataOfCollectionByAnyField(
            'forms',
            'trainerRef',
            '==',
            trainerRef
          )
          .pipe(
            take(1),
            map((resp) =>
              resp.docs
                .map((doc) => ({
                  ...(doc.data() as ReviewFormFirebaseResponse),
                  id: doc.id
                }))
                .map((data) => {
                  delete data.trainerRef;
                  return {
                    ...data,
                    updatedDate: new Date(
                      (data.updatedDate as any).seconds * 1000
                    )
                  };
                })
            )
          );
      }),
      map((reviews) => ({
        type: fromReviewsActions.LOAD_REVIEWS_FORMS_SUCCESS,
        data: reviews
      })),
      catchError((error) =>
        of({ type: fromReviewsActions.LOAD_REVIEWS_FORMS_FAIL, error })
      )
    )
  );

  // Load Reviews
  loadReviews$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.LoadReviewsAction),
      switchMap(
        ({ trainer, filters }: { trainer: User; filters?: ReviewsFilter }) =>
          this.getReviewsForTrainer(trainer, filters).pipe(
            switchMap((reviews) => this.addUsersToReviews(reviews, trainer)),
            map((reviews) => ({
              type: fromReviewsActions.LOAD_REVIEWS_SUCCESS,
              data: reviews
            })),
            catchError((error) =>
              of({ type: fromReviewsActions.LOAD_REVIEWS_FAIL, error })
            )
          )
      )
    )
  );

  // Add Review Form
  addReviewForm$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.AddFormAction),
      switchMap(
        async ({ trainer, data }: { trainer: User; data: ReviewForm }) => {
          const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;
          const newForm = { ...data, trainerRef };
          const result = await this.firebaseService.addDoc('forms', newForm);
          this.router.navigate(['/reviews']);
          return { ...data, id: result.id };
        }
      ),
      map((reviews) => ({
        type: fromReviewsActions.ADD_REVIEWS_SUCCESS,
        data: reviews
      })),
      catchError((error) =>
        of({ type: fromReviewsActions.ADD_REVIEWS_FAIL, error })
      )
    )
  );

  // Edit Review Form
  editReviewForm$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.EditFormAction),
      switchMap(
        async ({ trainer, data }: { trainer: User; data: ReviewForm }) => {
          const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;
          const newForm = { ...data, trainerRef };
          delete newForm.id;
          await this.firebaseService.updateDoc('forms', data.id!, newForm);
          this.router.navigate(['/reviews']);
          return { ...data, id: data.id };
        }
      ),
      map((reviewForms) => ({
        type: fromReviewsActions.EDIT_REVIEWS_SUCCESS,
        data: reviewForms
      })),
      catchError((error) =>
        of({ type: fromReviewsActions.EDIT_REVIEWS_FAIL, error })
      )
    )
  );

  // Remove Review Form
  removeReviewForm$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.RemoveFormAction),
      switchMap(async ({ id }) => {
        await this.firebaseService.deleteDoc('forms', id);
        this.router.navigate(['/reviews']);
        return id;
      }),
      map((id) => ({
        type: fromReviewsActions.REMOVE_REVIEWS_SUCCESS,
        id
      })),
      catchError((error) =>
        of({ type: fromReviewsActions.REMOVE_REVIEWS_FAIL, error })
      )
    )
  );

  // Complete Review
  completeReview$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.CompleteReviewAction),
      switchMap(async ({ review }: { review: Review }) => {
        const trainerRef = this.firestore.doc(`users/${review.trainer!.id}`)
          .ref as DocumentReference;
        const userRef = this.firestore.doc(`users/${review.user!.id}`)
          .ref as DocumentReference;
        const formRef = this.firestore.doc(`forms/${review.form!.id}`)
          .ref as DocumentReference;

        const reviewToUpdate: Review = {
          ...review,
          reviewedDate: new Date(),
          state: States.COMPLETED,
          trainerRef,
          userRef,
          formRef
        };
        delete reviewToUpdate.id;
        delete reviewToUpdate.form;
        delete reviewToUpdate.trainer;
        delete reviewToUpdate.user;

        await this.firebaseService.updateDoc(
          'reviews',
          review.id!,
          reviewToUpdate
        );
        this.router.navigate(['/reviews']);
        return { ...review, reviewedDate: reviewToUpdate.reviewedDate };
      }),
      map((reviewForms) => ({
        type: fromReviewsActions.COMPLETE_REVIEWS_SUCCESS,
        data: reviewForms
      })),
      catchError((error) =>
        of({ type: fromReviewsActions.COMPLETE_REVIEWS_FAIL, error })
      )
    )
  );

  // Create Review
  createReview$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromReviewsActions.CompleteReviewSuccessAction),
      switchMap(async ({ data }: { data: Review }) => {
        const trainerRef = this.firestore.doc(`users/${data.trainer!.id}`)
          .ref as DocumentReference;
        const userRef = this.firestore.doc(`users/${data.user!.id}`)
          .ref as DocumentReference;
        const formRef = this.firestore.doc(`forms/${data.form!.id}`)
          .ref as DocumentReference;
        const daysToAdd = this.getFrequencyDays(
          data.user?.frequencyReviewType!
        );
        const reviewDate = new Date(data.reviewDate);
        reviewDate.setDate(reviewDate.getDate() + daysToAdd);
        reviewDate.setHours(0, 0, 0, 0);
        const reviewToAdd: Review = {
          description: data.description,
          state: States.USER_PENDING,
          updatedDate: new Date(),
          reviewDate,
          reviewedDate: null,
          answers: [],
          multimediaContent: [],
          trainerRef,
          userRef,
          formRef
        };

        const resp = await this.firebaseService.addDoc('reviews', reviewToAdd);
        this.router.navigate(['/reviews']);

        delete reviewToAdd.formRef;
        delete reviewToAdd.userRef;
        delete reviewToAdd.trainerRef;

        return { ...reviewToAdd, id: resp.id };
      }),
      map((reviewForms) => ({
        type: fromReviewsActions.CREATE_REVIEWS_SUCCESS
      })),
      catchError((error) =>
        of({ type: fromReviewsActions.CREATE_REVIEWS_FAIL, error })
      )
    )
  );

  private getReviewsForTrainer(
    trainer: User,
    filters?: ReviewsFilter
  ): Observable<ReviewFirebaseResponse[]> {
    const queryFn: QueryFn = (ref) => {
      let query: Query = ref;
      if (filters?.id) {
        query = query.where(
          firebase.firestore.FieldPath.documentId(),
          '==',
          filters.id
        );
      }
      if (filters?.state) {
        query = query.where('state', '==', filters.state);
      }
      if (filters?.description) {
        const start = filters.description;
        const end = filters.description + '\uf8ff'; // `'\uf8ff'` es el último carácter en Unicode para cubrir todos los valores posibles

        query = query
          .where('description', '>=', start)
          .where('description', '<', end);
      }
      if (filters?.user) {
        const userRef = this.firestore.doc(`users/${filters.user}`).ref;
        query = query.where('userRef', '==', userRef);
      }
      const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;
      query = query.where('trainerRef', '==', trainerRef);

      return query;
    };

    return this.firebaseService
      .getDataOfCollectionByMultipleFields(this.collectionName, queryFn)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => ({
            ...(doc.data() as ReviewFirebaseResponse),
            id: doc.id
          }))
        )
      );
  }

  private addUsersToReviews(
    reviews: ReviewFirebaseResponse[],
    trainer: User
  ): Observable<Review[]> {
    let reviewsMapped: Review[] = [];
    if (reviews?.length > 0) {
      const usersAlreadyAdded: string[] = [];
      const userObservables = reviews
        .map((review) => {
          if (!usersAlreadyAdded.includes(review.userRef!.id)) {
            usersAlreadyAdded.push(review.userRef!.id);
            return from(review.userRef!.get());
          }
          return null;
        })
        .filter((users) => !!users);

      const formsAlreadyAdded: string[] = [];
      const formsObservables = reviews
        .map((review) => {
          if (!formsAlreadyAdded.includes(review.formRef!.id)) {
            formsAlreadyAdded.push(review.formRef!.id);
            return from(review.formRef!.get());
          }
          return null;
        })
        .filter((forms) => !!forms);

      const userResults$ = forkJoin(userObservables);
      const formResults$ = forkJoin(formsObservables);

      return forkJoin([userResults$, formResults$]).pipe(
        map(
          ([users, forms]) =>
            (reviewsMapped = reviews.map((review) => {
              const user: User = {
                ...users
                  .find((userDoc) => userDoc.id === review.userRef!.id)
                  ?.data(),
                id: review.userRef!.id
              } as any;

              const form: ReviewFormFirebaseResponse = {
                ...forms
                  .find((formDoc) => formDoc.id === review.formRef!.id)
                  ?.data(),
                id: review.formRef!.id
              } as any;

              delete review.userRef;
              delete review.trainerRef;
              delete review.formRef;

              delete user.trainerRef;
              delete user.teamRef;

              delete form.trainerRef;

              return {
                ...review,
                reviewDate: new Date(review.reviewDate.seconds * 1000),
                reviewedDate: review.reviewedDate
                  ? new Date(review.reviewDate.seconds * 1000)
                  : null,
                updatedDate: review.updatedDate
                  ? new Date(review.updatedDate.seconds * 1000)
                  : null,
                trainer,
                user,
                form
              } as Review;
            }))
        )
      );
    }
    return of(reviewsMapped);
  }

  private getFrequencyDays(subscriptionType: FrequencyReviewsTypes): number {
    switch (subscriptionType) {
      case FrequencyReviewsTypes.ONE_WEEK:
        return 7;
      case FrequencyReviewsTypes.TWO_WEEKS:
        return 14;
      case FrequencyReviewsTypes.THREE_WEEKS:
        return 21;
      case FrequencyReviewsTypes.FOUR_WEEKS:
        return 28;
      default:
        throw new Error('No valid frequency');
    }
  }
}
